import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { SearchState, SearchContextValue, SearchLocation } from '@/types';

const initialState: SearchState = {
  location: null,
  locationName: null,
  hasSearched: false,
};

interface ExtendedSearchContextValue extends SearchContextValue {
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
}

const SearchContext = createContext<ExtendedSearchContextValue | undefined>(undefined);

export function SearchProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SearchState>(initialState);
  const [isExpanded, setIsExpanded] = useState(true);

  const setLocation = useCallback((location: SearchLocation | null, name: string | null) => {
    setState((prev) => ({
      ...prev,
      location,
      locationName: name,
      hasSearched: location !== null,
    }));
    // Collapse the panel after search
    if (location !== null) {
      setIsExpanded(false);
    }
  }, []);

  const clearSearch = useCallback(() => {
    setState(initialState);
    setIsExpanded(true);
  }, []);

  const value: ExtendedSearchContextValue = {
    ...state,
    setLocation,
    clearSearch,
    isExpanded,
    setIsExpanded,
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
}

export function useSearch(): ExtendedSearchContextValue {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
}
